import { ComponentType, LazyExoticComponent, lazy } from 'react';

export type LazyWithPrefetchExoticComponent<Props> = LazyExoticComponent<ComponentType<Props>> & {
  preload?: () => Promise<{ default: ComponentType<Props> }>;
};

function isModuleNotFoundError(error?: unknown): boolean {
  if (!(error instanceof Error) || typeof error.message !== 'string') return false;
  return (
    // Chrome
    error.message.startsWith('Failed to fetch dynamically imported module') ||
    // Firefox
    error.message.startsWith('error loading dynamically imported module') ||
    // Safari
    error.message.startsWith('Importing a module script failed')
  );
}

/**
 * Lazy load a component with preloading
 * @param importFn - The function to import the component
 * @returns The lazy loaded component
 */
export function lazyWithPreload<Props>(
  importFn: () => Promise<{ default: React.ComponentType<Props> }>,
  validator?: () => Promise<boolean>,
) {
  const getFactory = (isPrefetch = false) => {
    let isFetched = false;
    let result: { default: React.ComponentType<Props> } = { default: () => null };

    return async () => {
      if (isPrefetch && isFetched) return result;

      try {
        const isValid = await validator?.();
        if (isValid === false) return result;

        result = await importFn();

        isFetched = true;
      } catch (error) {
        if (isPrefetch) return result;
        if (!isModuleNotFoundError(error)) throw error;

        if (
          error instanceof Error &&
          typeof window !== 'undefined' &&
          typeof sessionStorage !== 'undefined'
        ) {
          const storageKey = `lazy-with-preload-reload:${error.message}`;
          if (sessionStorage.getItem(storageKey)) throw error;

          sessionStorage.setItem(storageKey, '1');
          window.location.reload();

          return result;
        }

        throw error;
      }

      return result;
    };
  };

  const Component = lazy(getFactory()) as LazyWithPrefetchExoticComponent<Props>;

  Component.preload = getFactory(true);

  return Component;
}

export default {};
